// Página Contatos (M11 P2): lista de contatos únicos do time + grupos.
// Layout inbox: coluna esquerda com filtro grupo, direita com tabela.
// Ações: adicionar/editar/excluir contato, criar/editar/excluir grupo.
// Clique na linha abre Drawer com histórico de cobranças do contato.
// Todo pagador vira contato automaticamente ao criar cobrança (backend
// upsertFinanceContact), então botão "Importar" foi removido.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Button, Typography, Space, Input, message, Modal, Form,
  Tag, Row, Col, Empty, Select, Popconfirm, Drawer, Statistic, Spin
} from 'antd';
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, UserAddOutlined,
  TeamOutlined, HistoryOutlined, CheckCircleTwoTone, ClockCircleTwoTone,
  CloseCircleTwoTone, MailOutlined, PhoneOutlined
} from '@ant-design/icons';
import api from '../../../services/api';
import { getActiveTeamId } from '../../../utils/activeTeam';

const { Title, Text } = Typography;
const PRIMARY = '#128C7E';
const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';

const STATUS_META = {
  paid: { color: 'success', icon: <CheckCircleTwoTone twoToneColor="#52c41a" />, label: 'Pago' },
  sent: { color: 'processing', icon: <ClockCircleTwoTone twoToneColor="#1677ff" />, label: 'Enviado' },
  pending: { color: 'default', icon: <ClockCircleTwoTone twoToneColor="#8c8c8c" />, label: 'Pendente' },
  error: { color: 'error', icon: <CloseCircleTwoTone twoToneColor="#ff4d4f" />, label: 'Erro' },
  expired: { color: 'warning', icon: <CloseCircleTwoTone twoToneColor="#faad14" />, label: 'Expirada' },
  refunded: { color: 'warning', icon: <CloseCircleTwoTone twoToneColor="#faad14" />, label: 'Reembolsada' },
};

export default function FinanceContactsPage() {
  const teamId = getActiveTeamId();
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [contactModal, setContactModal] = useState({ open: false, contact: null });
  const [groupModal, setGroupModal] = useState({ open: false, group: null });
  const [historyDrawer, setHistoryDrawer] = useState({ open: false, contact: null, loading: false, data: null });
  const [contactForm] = Form.useForm();
  const [groupForm] = Form.useForm();

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const [c, g] = await Promise.all([
        api.get('/community/finance/contacts', {
          params: { team_id: teamId, search: search || undefined, group_id: selectedGroupId || undefined },
        }),
        api.get('/community/finance/contact-groups', { params: { team_id: teamId } }),
      ]);
      setContacts(c.data.items || []);
      setGroups(g.data.items || []);
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou carregar');
    } finally { setLoading(false); }
  }, [teamId, search, selectedGroupId]);
  useEffect(() => { load(); }, [load]);

  const openNewContact = () => { contactForm.resetFields(); setContactModal({ open: true, contact: null }); };
  const openEditContact = (c) => {
    contactForm.setFieldsValue({
      name: c.name, email: c.email, phone: c.phone, notes: c.notes,
      group_ids: c.group_ids || [],
    });
    setContactModal({ open: true, contact: c });
  };
  const saveContact = async () => {
    try {
      const v = await contactForm.validateFields();
      let savedContactId;
      const previousGroupIds = new Set(contactModal.contact?.group_ids || []);
      if (contactModal.contact) {
        await api.put(`/community/finance/contacts/${contactModal.contact.id}`, {
          team_id: teamId, ...v,
        });
        savedContactId = contactModal.contact.id;
      } else {
        const r = await api.post('/community/finance/contacts', { team_id: teamId, ...v });
        savedContactId = r.data?.id || r.data?.contact?.id;
      }
      message.success(contactModal.contact ? 'Contato atualizado' : 'Contato adicionado');
      setContactModal({ open: false, contact: null });
      load();

      // Se contato entrou em grupo(s) NOVOS que tem recorrência ativa já
      // rodada no mês, oferecer cobrar esse mês.
      const newGroupIds = (v.group_ids || []).filter(id => !previousGroupIds.has(id));
      if (savedContactId && newGroupIds.length > 0) {
        try {
          const catch_ = await api.get('/community/finance/charges/recurrences/pending-catchup',
            { params: { team_id: teamId, contact_id: savedContactId } });
          const pending = (catch_.data?.items || []).filter(x => newGroupIds.includes(x.source_group_id));
          for (const p of pending) {
            await new Promise((resolve) => {
              Modal.confirm({
                title: 'Cobrar esse mês também?',
                content: (
                  <div>
                    <p>A recorrência <b>{p.template_name}</b> já rodou esse mês (dia atual).</p>
                    <p>Quer cobrar <b>{fmtBRL(p.amount_brl)}</b> desse contato agora, referente ao mês corrente? Vai gastar 1 crédito.</p>
                    <p><Text type="secondary">Se preferir, ele começa a ser cobrado só no próximo ciclo.</Text></p>
                  </div>
                ),
                okText: 'Cobrar agora',
                cancelText: 'Só no próximo mês',
                onOk: async () => {
                  try {
                    await api.post(`/community/finance/charges/recurrences/${p.recurrence_id}/catchup-charge`,
                      { team_id: teamId, contact_id: savedContactId, ref_year: p.ref_year, ref_month: p.ref_month });
                    message.success('Cobrança enviada pro contato');
                  } catch (err) {
                    message.error(err.response?.data?.message || 'Falhou cobrar');
                  } finally { resolve(); }
                },
                onCancel: () => resolve(),
              });
            });
          }
        } catch { /* silencia — feature secundária */ }
      }
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.message || 'Falhou salvar');
    }
  };
  const deleteContact = async (id) => {
    try {
      await api.delete(`/community/finance/contacts/${id}`, { params: { team_id: teamId } });
      message.success('Contato excluído');
      load();
    } catch (e) { message.error(e.response?.data?.message || 'Falhou excluir'); }
  };

  const openNewGroup = () => { groupForm.resetFields(); setGroupModal({ open: true, group: null }); };
  const openEditGroup = (g) => {
    groupForm.setFieldsValue({ name: g.name, description: g.description });
    setGroupModal({ open: true, group: g });
  };
  const saveGroup = async () => {
    try {
      const v = await groupForm.validateFields();
      if (groupModal.group) {
        await api.put(`/community/finance/contact-groups/${groupModal.group.id}`, { team_id: teamId, ...v });
      } else {
        await api.post('/community/finance/contact-groups', { team_id: teamId, ...v });
      }
      message.success(groupModal.group ? 'Grupo atualizado' : 'Grupo criado');
      setGroupModal({ open: false, group: null });
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.message || 'Falhou salvar');
    }
  };
  const deleteGroup = async (id) => {
    try {
      await api.delete(`/community/finance/contact-groups/${id}`, { params: { team_id: teamId } });
      message.success('Grupo excluído');
      if (selectedGroupId === id) setSelectedGroupId(null);
      load();
    } catch (e) { message.error(e.response?.data?.message || 'Falhou excluir'); }
  };

  const openHistory = async (contact) => {
    setHistoryDrawer({ open: true, contact, loading: true, data: null });
    try {
      const r = await api.get(`/community/finance/contacts/${contact.id}/charges`, {
        params: { team_id: teamId },
      });
      setHistoryDrawer(prev => ({ ...prev, loading: false, data: r.data }));
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou carregar histórico');
      setHistoryDrawer(prev => ({ ...prev, loading: false }));
    }
  };
  const closeHistory = () => setHistoryDrawer({ open: false, contact: null, loading: false, data: null });

  if (!teamId) return <div style={{ padding: 32, textAlign: 'center' }}>Escolha um time no seletor.</div>;

  return (
    <div style={{ padding: '16px 12px', maxWidth: 1400, margin: '0 auto' }}>
      <Row justify="space-between" align="middle" wrap gutter={[8, 8]} style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Contatos</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Todo pagador vira contato automaticamente. Clique num contato pra ver o histórico.
          </Text>
        </Col>
        <Col>
          <Button type="primary" icon={<UserAddOutlined />}
            style={{ background: PRIMARY, borderColor: PRIMARY }}
            onClick={openNewContact}>
            Novo contato
          </Button>
        </Col>
      </Row>

      <Row gutter={[12, 12]}>
        {/* GRUPOS à esquerda */}
        <Col xs={24} md={7}>
          <Card
            size="small"
            title={<Space><TeamOutlined /><Text strong>Grupos ({groups.length})</Text></Space>}
            styles={{ header: { padding: '10px 12px' }, body: { padding: 8 } }}
            extra={<Button size="small" icon={<PlusOutlined />} onClick={openNewGroup}>Novo</Button>}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
              <div
                onClick={() => setSelectedGroupId(null)}
                style={{
                  padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                  background: !selectedGroupId ? '#f0faf7' : '#fff',
                  borderLeft: `3px solid ${!selectedGroupId ? PRIMARY : 'transparent'}`,
                }}
              >
                <Text strong style={{ fontSize: 13 }}>Todos os contatos</Text>
                <div><Text type="secondary" style={{ fontSize: 11 }}>{contacts.length}</Text></div>
              </div>
              {groups.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={<Text type="secondary" style={{ fontSize: 12 }}>Sem grupos ainda</Text>} />
              ) : groups.map(g => (
                <div
                  key={g.id}
                  onClick={() => setSelectedGroupId(g.id)}
                  style={{
                    padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                    background: selectedGroupId === g.id ? '#f0faf7' : '#fff',
                    borderLeft: `3px solid ${selectedGroupId === g.id ? PRIMARY : 'transparent'}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong style={{ fontSize: 13, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>{g.member_count} contato(s)</Text>
                  </div>
                  <Space size={2}>
                    <Button type="text" size="small" icon={<EditOutlined />}
                      onClick={e => { e.stopPropagation(); openEditGroup(g); }} />
                    <Popconfirm title="Excluir grupo?" onConfirm={() => deleteGroup(g.id)}>
                      <Button type="text" size="small" danger icon={<DeleteOutlined />}
                        onClick={e => e.stopPropagation()} />
                    </Popconfirm>
                  </Space>
                </div>
              ))}
            </Space>
          </Card>
        </Col>

        {/* CONTATOS à direita */}
        <Col xs={24} md={17}>
          <Card
            size="small"
            title={
              <Input
                placeholder="Buscar contato…"
                prefix={<SearchOutlined />}
                value={search}
                onChange={e => setSearch(e.target.value)}
                allowClear
                size="small"
                style={{ width: 200 }}
              />
            }
            styles={{ header: { padding: '10px 12px' }, body: { padding: 12 } }}
          >
            <div style={{ overflowX: 'auto' }}>
              <Table
                rowKey="id"
                size="small"
                loading={loading}
                dataSource={contacts}
                scroll={{ x: 600 }}
                pagination={{ pageSize: 20, simple: true, size: 'small' }}
                onRow={(record) => ({
                  onClick: () => openHistory(record),
                  style: { cursor: 'pointer' },
                })}
                columns={[
                  { title: 'Nome', dataIndex: 'name', width: 160,
                    render: v => <Text strong>{v}</Text> },
                  { title: 'Email', dataIndex: 'email', width: 200,
                    render: v => v || <Text type="secondary">—</Text> },
                  { title: 'Telefone', dataIndex: 'phone', width: 120,
                    render: v => v || <Text type="secondary">—</Text> },
                  { title: 'Grupos', dataIndex: 'group_names', width: 180,
                    render: names => names?.length > 0
                      ? names.map((n, i) => <Tag key={i} color="blue" style={{ fontSize: 10 }}>{n}</Tag>)
                      : <Text type="secondary">—</Text> },
                  { title: '', key: 'act', width: 90, render: (_, r) => (
                    <Space size={2} onClick={e => e.stopPropagation()}>
                      <Button type="text" size="small" icon={<HistoryOutlined />}
                        title="Histórico" onClick={() => openHistory(r)} />
                      <Button type="text" size="small" icon={<EditOutlined />}
                        title="Editar" onClick={() => openEditContact(r)} />
                      <Popconfirm title="Excluir contato?" onConfirm={() => deleteContact(r.id)}>
                        <Button type="text" size="small" danger icon={<DeleteOutlined />}
                          title="Excluir" />
                      </Popconfirm>
                    </Space>
                  ) },
                ]}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* MODAL contato */}
      <Modal
        open={contactModal.open}
        title={contactModal.contact ? 'Editar contato' : 'Novo contato'}
        onCancel={() => setContactModal({ open: false, contact: null })}
        onOk={saveContact}
        okText="Salvar"
        cancelText="Cancelar"
        okButtonProps={{ style: { background: PRIMARY, borderColor: PRIMARY } }}
        destroyOnClose
      >
        <Form form={contactForm} layout="vertical" preserve={false}>
          <Form.Item label="Nome" name="name" rules={[{ required: true, message: 'Obrigatório' }, { max: 200 }]}>
            <Input placeholder="Nome completo" />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item label="Email" name="email" rules={[{ type: 'email', message: 'Email inválido' }]}>
                <Input placeholder="email@..." />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Telefone" name="phone">
                <Input placeholder="5511..." />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Grupos" name="group_ids">
            <Select mode="multiple" placeholder="Selecione grupos"
              options={groups.map(g => ({ value: g.id, label: g.name }))} />
          </Form.Item>
          <Form.Item label="Anotação (opcional)" name="notes">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      {/* DRAWER histórico do contato */}
      <Drawer
        open={historyDrawer.open}
        onClose={closeHistory}
        title={historyDrawer.contact ? (
          <Space direction="vertical" size={0}>
            <Text strong>{historyDrawer.contact.name}</Text>
            <Space size={12} wrap>
              {historyDrawer.contact.email && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <MailOutlined /> {historyDrawer.contact.email}
                </Text>
              )}
              {historyDrawer.contact.phone && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <PhoneOutlined /> {historyDrawer.contact.phone}
                </Text>
              )}
            </Space>
          </Space>
        ) : 'Histórico'}
        width={Math.min(720, window.innerWidth - 32)}
        destroyOnClose
      >
        {historyDrawer.loading ? (
          <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>
        ) : historyDrawer.data ? (
          <>
            {/* Resumo em cards */}
            <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={8}>
                <Card size="small" styles={{ body: { padding: 12 } }}>
                  <Statistic
                    title={<Text style={{ fontSize: 11 }}>Total cobranças</Text>}
                    value={historyDrawer.data.summary.total_charges}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8}>
                <Card size="small" styles={{ body: { padding: 12 } }}>
                  <Statistic
                    title={<Text style={{ fontSize: 11 }}>Pagas</Text>}
                    value={historyDrawer.data.summary.paid_count}
                    valueStyle={{ fontSize: 20, color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small" styles={{ body: { padding: 12 } }}>
                  <Statistic
                    title={<Text style={{ fontSize: 11 }}>Total pago</Text>}
                    value={historyDrawer.data.summary.paid_amount_brl}
                    precision={2}
                    prefix="R$"
                    valueStyle={{ fontSize: 20, color: PRIMARY }}
                  />
                </Card>
              </Col>
            </Row>

            {historyDrawer.data.items.length === 0 ? (
              <Empty description="Nenhuma cobrança ainda pra esse contato" />
            ) : (
              <Table
                rowKey="id"
                size="small"
                dataSource={historyDrawer.data.items}
                pagination={{ pageSize: 20, simple: true, size: 'small' }}
                columns={[
                  { title: 'Data', dataIndex: 'created_at', width: 90,
                    render: v => <Text style={{ fontSize: 12 }}>{fmtDate(v)}</Text> },
                  { title: 'Valor', dataIndex: 'amount_brl', width: 90, align: 'right',
                    render: (v, r) => (
                      <div style={{ textAlign: 'right' }}>
                        <Text strong style={{ fontSize: 12 }}>{fmtBRL(v)}</Text>
                        {r.status === 'paid' && Number(r.paid_amount_brl) !== Number(v) && (
                          <div><Text type="secondary" style={{ fontSize: 10 }}>pago {fmtBRL(r.paid_amount_brl)}</Text></div>
                        )}
                      </div>
                    ) },
                  { title: 'Status', dataIndex: 'status', width: 100,
                    render: (v, r) => {
                      const meta = STATUS_META[v] || { color: 'default', icon: null, label: v };
                      return (
                        <div>
                          <Tag color={meta.color} icon={meta.icon} style={{ fontSize: 10 }}>{meta.label}</Tag>
                          {r.paid_at && (
                            <div><Text type="secondary" style={{ fontSize: 10 }}>{fmtDate(r.paid_at)}</Text></div>
                          )}
                        </div>
                      );
                    } },
                  { title: 'Método', dataIndex: 'paid_method', width: 80,
                    render: v => v ? <Text style={{ fontSize: 11 }}>{v.toUpperCase()}</Text> : <Text type="secondary" style={{ fontSize: 11 }}>—</Text> },
                  { title: 'Lote', dataIndex: 'batch_name', width: 140,
                    render: (v, r) => (
                      <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                        {v || `#${r.batch_id}`}
                      </Text>
                    ) },
                ]}
              />
            )}
          </>
        ) : null}
      </Drawer>

      {/* MODAL grupo */}
      <Modal
        open={groupModal.open}
        title={groupModal.group ? 'Editar grupo' : 'Novo grupo'}
        onCancel={() => setGroupModal({ open: false, group: null })}
        onOk={saveGroup}
        okText="Salvar"
        cancelText="Cancelar"
        okButtonProps={{ style: { background: PRIMARY, borderColor: PRIMARY } }}
        destroyOnClose
      >
        <Form form={groupForm} layout="vertical" preserve={false}>
          <Form.Item label="Nome" name="name" rules={[{ required: true, message: 'Obrigatório' }, { max: 100 }]}>
            <Input placeholder="Ex: Mensalistas" />
          </Form.Item>
          <Form.Item label="Descrição" name="description">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
